/**
 * "Just you and the host": the private conversation. The thread with replies under their message, the
 * pill for what arrived while reading above, the press-and-hold menu, the composer, and a quick live
 * the person was in as its call card. Pinned and the next live are on the right.
 */
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "../../components/ui/Icon";
import { Composer } from "./Composer";
import { usePage } from "./context";
import { Content, Replies, Row } from "./Bubbles";
import { Frame, useMarkSeen } from "./Frame";
import { IcCopy, IcDl, IcDown, IcHeartF, IcPin, IcReply } from "./icons";
import type { Entry, Line, QuickCall } from "./model";
import { CallCard, Head, Nav, NextLive } from "./Parts";
import { ContinueCard, PinLine, PinList } from "./Pinned";
import { HostAvatar } from "./Side";
import { callWords, liveWhen } from "./talk";
import { seatHref, useCalendarMenu } from "./Pinned";

const NEAR_PX = 140;

function Typing(): ReactNode {
  const { host } = usePage();
  return (
    <div className="row maya typing in gfirst glast">
      <div className="av">{host.avatar ? <img src={host.avatar} alt="" /> : null}</div>
      <div className="stack">
        <div className="mw">
          <div className="bub t">
            <span className="dots" aria-label={`${host.first} is typing`}>
              <i />
              <i />
              <i />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The answers to a host's question, until the person has picked one. */
function QuestionChips({ entry }: { entry: Entry }): ReactNode {
  const { convo, answerQuestion } = usePage();
  const q = entry.message?.question;
  if (!q || entry.from !== "host") return null;
  const answered = [...convo.byId.values()].some((e) => e.message?.answer_to === entry.id);
  return (
    <div className={`chips${answered ? " gone" : ""}`}>
      {q.options.map((o) => (
        <button key={o} type="button" className="chip" onClick={() => answerQuestion(entry.id, entry.webinar_id, o)}>
          {o}
        </button>
      ))}
    </div>
  );
}

/** The menu a long press or a right click opens on a message: Love above it, Reply, Pin and Copy below. */
function HoldMenu({ hold, onClose }: { hold: { entry: Entry; rect: DOMRect; conv: DOMRect }; onClose: () => void }): ReactNode {
  const { love, replyTo, isPinned, togglePin, toast } = usePage();
  const { entry, rect, conv } = hold;
  const text = entry.message?.body || entry.comment?.body || "";
  const pinned = isPinned(entry);
  const items = text ? 3 : 2;
  const mh = items * 46 + 12;
  // Why: as in Messages, Love sits above the lifted bubble and the menu below it; the bubble slides up when it sits too low.
  const topMin = entry.from === "host" ? 72 : 16;
  const top = Math.max(topMin, Math.min(rect.top - conv.top, conv.height - 16 - mh - 12 - rect.height));
  const [lifted, setLifted] = useState(rect.top - conv.top);
  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setLifted(top));
    return () => window.cancelAnimationFrame(raf);
  }, [top]);
  const mx = entry.from === "me" ? Math.max(12, rect.right - conv.left - 220) : Math.min(Math.max(12, rect.left - conv.left), conv.width - 232);
  const loved = Boolean(entry.post?.liked ?? entry.message?.liked);
  return (
    <div className="ctx">
      <div className="ctx-scrim" onClick={onClose} />
      <div
        className={`row ${entry.from === "host" ? "maya" : "me"} glast ctx-row`}
        style={{ left: rect.left - conv.left, top: lifted, width: rect.width, transition: "top .32s cubic-bezier(.3,1.2,.5,1)" }}
      >
        <div className="mw">
          <Content entry={entry} />
        </div>
      </div>
      <div className="ctx-menu" style={{ left: mx, top: top + rect.height + 12 }}>
        <button
          type="button"
          onClick={() => {
            replyTo(entry);
            onClose();
          }}
        >
          Reply
          <IcReply s={17} />
        </button>
        <button
          type="button"
          onClick={() => {
            togglePin(entry);
            onClose();
          }}
        >
          {pinned ? "Unpin" : "Pin"}
          <IcPin s={16} />
        </button>
        {text ? (
          <button
            type="button"
            onClick={() => {
              try {
                void navigator.clipboard?.writeText(text).catch(() => {});
              } catch {
                /* the clipboard is not ours here */
              }
              toast("Copied");
              onClose();
            }}
          >
            Copy
            <IcCopy s={16} />
          </button>
        ) : null}
      </div>
      {entry.from === "host" ? (
        <div className="ctx-tb" style={{ left: Math.max(12, rect.left - conv.left), top: top - 60 }}>
          <button
            type="button"
            className={loved ? "on" : ""}
            aria-label="Love"
            onClick={() => {
              love(entry);
              onClose();
            }}
          >
            <IcHeartF s={18} />
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** How a host email is marked in the thread: "Email · subject", or the update's title when it has none. A private update is an email too. */
function emailMark(e: Entry): string | null {
  const title = e.message ? e.message.email_subject : e.post?.title?.trim() || "Update";
  if (title === undefined) return null;
  return title ? `Email · ${title}` : "Email";
}

export function Conversation(): ReactNode {
  const p = usePage();
  const { convo, host, typing, phone, data, retry, guest, place, replying, lay } = p;
  const conv = useRef<HTMLElement>(null);
  const thread = useRef<HTMLDivElement>(null);
  const cwrap = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [jump, setJump] = useState(false);
  const [pill, setPill] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [hold, setHold] = useState<{ entry: Entry; rect: DOMRect; conv: DOMRect } | null>(null);
  const [dropping, setDropping] = useState(false);
  const [dropped, setDropped] = useState<File[] | null>(null);

  // --- the composer sets how far the thread ends ---
  useLayoutEffect(() => {
    const el = conv.current;
    if (!el) return;
    const measure = () => el.style.setProperty("--comp-h", `${cwrap.current?.offsetHeight ?? 78}px`);
    measure();
    const ro = new ResizeObserver(measure);
    if (cwrap.current) ro.observe(cwrap.current);
    return () => ro.disconnect();
  }, []);

  useMarkSeen(place, true, guest.directUnread ? 1 : 0);
  useEffect(() => {
    if (replying?.kind === "announcement") document.querySelector<HTMLTextAreaElement>(".tp #tp-ta")?.focus();
  }, [replying]);

  const near = () => {
    const t = thread.current;
    return !t || t.scrollHeight - t.scrollTop - t.clientHeight < NEAR_PX;
  };
  const toBottom = useCallback((smooth: boolean) => {
    const t = thread.current;
    if (t) t.scrollTo({ top: t.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  // --- new lines: stay at the bottom when there, else the pill says something came ---
  const lines = convo.lines;
  const last = lines.at(-1);
  const lastKey = last?.type === "entry" ? `${last.entry.key}:${last.entry.replies.length}` : "";
  const prevLast = useRef<string | null>(null);
  const wasNear = useRef(true);
  useLayoutEffect(() => {
    if (prevLast.current === null) {
      toBottom(false);
      prevLast.current = lastKey;
      return;
    }
    if (lastKey === prevLast.current) return;
    prevLast.current = lastKey;
    const mine = last?.type === "entry" && last.entry.from === "me";
    if (mine || wasNear.current) window.requestAnimationFrame(() => toBottom(true));
    else setPill("bottom");
  }, [lastKey, last, toBottom]);
  // Whatever grows at the foot while they are there (a receipt, a picture loading, the typing dots) keeps them there.
  const tin = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = tin.current;
    if (!el) return;
    // Why the height before the change and today's scroll: a scroll and a resize can land between two scroll events.
    let before = el.offsetHeight;
    const ro = new ResizeObserver(() => {
      const t = thread.current;
      const grew = el.offsetHeight;
      if (t && t.scrollTop + t.clientHeight >= before + t.scrollHeight - grew - NEAR_PX && grew !== before) toBottom(false);
      before = grew;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [toBottom]);

  const onScroll = () => {
    const t = thread.current;
    if (!t) return;
    setScrolled(t.scrollTop > 6);
    wasNear.current = near();
    if (pill && wasNear.current) setPill(null);
    setJump(!pill && t.scrollHeight - t.scrollTop - t.clientHeight > 380);
  };

  // --- the receipt sits on the person's last message ---
  let lastMine: Entry | null = null;
  for (const l of lines) {
    if (l.type !== "entry") continue;
    if (l.entry.from === "me") lastMine = l.entry;
    for (const r of l.entry.replies) if (r.from === "me" && (!lastMine || r.at >= lastMine.at)) lastMine = r;
  }
  const readIn = new Set(data.sessions.filter((s) => s.host_read === 1).map((s) => s.webinar_id));
  const receiptFor = (e: Entry): ReactNode => {
    if (e !== lastMine) return null;
    const o = e.outgoing;
    if (o?.state === "failed")
      return (
        <button type="button" className="receipt" onClick={() => retry(o.cid)}>
          Not sent. Tap to try again.
        </button>
      );
    // Why the key: a receipt that changes (a new message, Delivered to Read) is drawn afresh and fades in once.
    const words = o && o.state !== "sent" ? "Sending" : readIn.has(e.webinar_id) ? "Read" : "Delivered";
    return (
      <div key={`${e.key}:${words}`} className="receipt fresh">
        {words}
      </div>
    );
  };
  const failedReceipt = (e: Entry): ReactNode =>
    e.outgoing?.state === "failed" && e !== lastMine ? (
      <button type="button" className="receipt" onClick={() => retry(e.outgoing?.cid ?? "")}>
        Not sent. Tap to try again.
      </button>
    ) : null;

  const rowProps = {
    selected: false,
    onSelect: setSelected,
    onHold: (entry: Entry, el: HTMLElement) => {
      const c = conv.current?.getBoundingClientRect();
      if (c) setHold({ entry, rect: el.getBoundingClientRect(), conv: c });
    },
  };

  const renderList = (list: Entry[], inThread: boolean) =>
    list.map((e, i) => {
      const prev = list[i - 1];
      const next = list[i + 1];
      return (
        <Row
          key={e.key}
          entry={e}
          first={!prev || prev.from !== e.from}
          last={!next || next.from !== e.from}
          inThread={inThread}
          receipt={receiptFor(e) ?? failedReceipt(e)}
          {...rowProps}
          selected={selected === e.key}
        />
      );
    });

  // Group the top-level lines: consecutive rows from one side group, a separator or a fold breaks it.
  const nodes: ReactNode[] = [];
  const tops = lines.filter((l): l is Extract<Line, { type: "entry" }> => l.type === "entry").map((l) => l.entry);
  const lastMeTop = [...tops].reverse().find((e) => e.from === "me");
  lines.forEach((l, i) => {
    if (l.type === "sep")
      nodes.push(
        <div className="sep" key={l.key}>
          <b>{l.day}</b>
          {l.joiner}
          {l.time}
        </div>,
      );
    else if (l.type === "call") nodes.push(<QuickCallCard key={l.key} call={l.call} />);
    else if (l.type === "unread")
      nodes.push(
        <div className="unread" key={l.key}>
          {l.count} new
        </div>,
      );
    else {
      const e = l.entry;
      const prev = lines[i - 1];
      const next = lines[i + 1];
      const groupedPrev = prev?.type === "entry" && prev.entry.from === e.from && !prev.entry.replies.length;
      const groupedNext = next?.type === "entry" && next.entry.from === e.from && !e.replies.length;
      const mark = emailMark(e);
      if (mark)
        nodes.push(
          <small className="jp-via" key={`via-${e.key}`}>
            <Icon name="mail" /> {mark}
          </small>,
        );
      nodes.push(
        <Row
          key={e.key}
          entry={e}
          first={!groupedPrev}
          last={!groupedNext}
          lastMe={e === lastMeTop && !groupedNext}
          receipt={receiptFor(e) ?? failedReceipt(e)}
          chips={<QuestionChips entry={e} />}
          {...rowProps}
          selected={selected === e.key}
        />,
      );
      if (e.replies.length)
        nodes.push(
          <Replies key={`ith-${e.key}`} entry={e}>
            {renderList(e.replies, true)}
          </Replies>,
        );
    }
  });

  const next = guest.nextLive;
  const room = next ? data.sessions.find((x) => x.webinar_id === next.webinarId) : undefined;
  // Why nothing on upright: its Pinned line and sheet hold all three, so the chat starts under the head.
  const right =
    lay === "upright" ? null : (
      <>
        <PinList />
        <ContinueCard />
        <NextLive next={next} />
      </>
    );
  return (
    <Frame
      nav={
        <Nav
          center={
            <span className="gq-who">
              <HostAvatar small />
              <b>{host.name}</b>
            </span>
          }
        />
      }
      strip={next ? <NextStrip next={next} room={room} /> : null}
      head={<Head icon="lock" title={`Just you and ${host.first}`} sub={`${host.first}'s messages and emails to you, and yours back`} />}
      right={right}
      under={<PinLine />}
      sheet={{
        ref: conv,
        className: `conv${scrolled ? " scrolled" : ""}${dropping ? " dropping" : ""}`,
        "aria-label": `Your conversation with ${host.first}`,
        onDragEnter: (e) => {
          if (![...e.dataTransfer.types].includes("Files")) return;
          e.preventDefault();
          setDropping(true);
        },
        onDragOver: (e) => {
          if ([...e.dataTransfer.types].includes("Files")) e.preventDefault();
        },
        onDragLeave: (e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setDropping(false);
        },
        onDrop: (e) => {
          if (![...e.dataTransfer.types].includes("Files")) return;
          e.preventDefault();
          setDropping(false);
          setDropped([...e.dataTransfer.files]);
        },
      }}
    >
      <div className="thread" ref={thread} onScroll={onScroll}>
        <div className="tin" ref={tin} role="log" aria-live="polite">
          {phone ? (
            <p className="gq-priv">
              <Icon name="lock" /> Only you and {host.first} see this
            </p>
          ) : null}
          {nodes}
          {typing ? <Typing /> : null}
        </div>
      </div>
      <button type="button" className={`jump${jump ? " on" : ""}`} aria-label="Go to the newest message" onClick={() => toBottom(true)}>
        <IcDown s={18} />
      </button>
      <button
        type="button"
        className={`newpill${pill ? " on" : ""}`}
        onClick={() => {
          setPill(null);
          toBottom(true);
        }}
      >
        {host.avatar ? <img src={host.avatar} alt="" /> : null}
        <span>New message</span>
        <IcDown s={15} />
      </button>
      <div className="cwrap" ref={cwrap}>
        <Composer dropped={dropped} onDropped={() => setDropped(null)} />
      </div>
      <div className="drop" aria-hidden="true">
        <div className="drop-in">
          <span className="drop-ic">
            <IcDl s={26} />
          </span>
          <b>Drop to send to <bdi>{host.first}</bdi></b>
        </div>
      </div>
      {hold ? <HoldMenu hold={hold} onClose={() => setHold(null)} /> : null}
    </Frame>
  );
}

/** A quick live the person was in: the call's card, with Watch once the replay is ready. */
function QuickCallCard({ call }: { call: QuickCall }): ReactNode {
  const { data, host, playTalk } = usePage();
  const room = data.sessions.find((s) => s.webinar_id === call.webinar_id);
  return (
    <CallCard
      pics={[room?.poster_url ?? host.avatar]}
      eyebrow={`Live with ${host.first}`}
      title={call.title}
      line={callWords(call.at, call.minutes)}
      action={
        call.replay === "ready" ? (
          <button type="button" className="jp-btn soft s" onClick={() => playTalk(call.webinar_id, { start: true })}>
            Watch
          </button>
        ) : (
          <span className="jp-call-wait">The replay is on its way</span>
        )
      }
    />
  );
}

/** A phone's strip under the head: the next live, and the way to add it to a calendar. */
function NextStrip({ next, room }: { next: NonNullable<ReturnType<typeof usePage>["guest"]["nextLive"]>; room: ReturnType<typeof usePage>["data"]["sessions"][number] | undefined }): ReactNode {
  const { token, nowMs, inCalendar, host } = usePage();
  const calendar = useCalendarMenu();
  const open = room?.live ? nowMs >= Date.parse(room.live.opensAt) : false;
  return (
    <div className="gq-strip">
      <span>
        <b>Live {liveWhen(next.startsAt, nowMs)}</b>
        <small>{room?.title ?? host.name}</small>
      </span>
      {room && open ? (
        <a className="gq-strip-b" href={seatHref(room, token)}>
          Join the room
        </a>
      ) : room?.calendar && !inCalendar(room.webinar_id) ? (
        <button type="button" className="gq-strip-b" data-menu-anchor onClick={(e) => calendar(e.currentTarget, room)}>
          Add to calendar
        </button>
      ) : null}
    </div>
  );
}
